import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MailIcon } from '@/components/catalog/icons';
import { TemplatePreviewPanel } from '@/components/notifications/admin/TemplatePreviewPanel';
import { flattenCatalogue } from '@/components/notifications/admin/log-labels';
import {
  Combobox,
  EmptyState,
  Field,
  PageHeader,
  Panel,
  Skeleton,
  type ComboboxOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import type { NotificationCatalogue } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/*
 * Branded notification email previews, one per catalogue event. The selected
 * event is `?event=<key>` (absent = the first event in the catalogue).
 */

const DENIED = 'Your permission set does not include this. An administrator can change that.';

export function NotificationTemplatesPage() {
  const { ability, user } = useAuth();
  const canRead = ability.canRead('settings-notifications');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedKey = searchParams.get('event') ?? '';

  const [catalogue, setCatalogue] = useState<NotificationCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (!canRead) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .getNotificationCatalogue(signal)
      .then((res) => {
        if (signal.aborted) return;
        setCatalogue(res.data);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
          return;
        }
        setError(caught instanceof Error ? caught.message : 'Failed to load the catalogue');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [canRead]);

  const entries = useMemo(() => flattenCatalogue(catalogue), [catalogue]);
  const selected = entries.find((entry) => entry.event.key === selectedKey) ?? entries[0] ?? null;

  const options = useMemo<ComboboxOption[]>(
    () =>
      entries.map((entry) => ({
        value: entry.event.key,
        label: entry.event.label,
        hint: `${entry.moduleLabel} · ${entry.submoduleLabel}`,
      })),
    [entries],
  );

  function select(key: string) {
    const next = new URLSearchParams(searchParams);
    if (key) next.set('event', key);
    else next.delete('event');
    setSearchParams(next, { replace: true });
  }

  if (!canRead || forbidden) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader title="Email templates" />
        <Panel>
          <EmptyState
            icon={<MailIcon className="h-6 w-6" />}
            title="Not available to your account"
            description={DENIED}
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader title="Email templates" />

      {error && (
        <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load the catalogue.</>}>
          <span className="[overflow-wrap:anywhere]">{error}</span>
        </Notice>
      )}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-[16rem_minmax(0,1fr)]">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : !selected ? (
        error ? null : (
          <Panel>
            <EmptyState
              icon={<MailIcon className="h-6 w-6" />}
              title="No notification events"
              description="The catalogue is empty."
            />
          </Panel>
        )
      ) : (
        <div className="grid min-w-0 gap-4 md:grid-cols-[16rem_minmax(0,1fr)] md:items-start">
          <div className="md:hidden">
            <Field label="Event" hideLabel>
              {({ id }) => (
                <Combobox
                  id={id}
                  value={selected.event.key}
                  onChange={select}
                  options={options}
                  searchPlaceholder="Search events…"
                />
              )}
            </Field>
          </div>

          <Panel className="hidden min-w-0 md:block">
            <nav aria-label="Notification events" className="max-h-[calc(100vh-10rem)] overflow-y-auto py-2">
              {catalogue?.modules.map((module) => (
                <div key={module.key} className="py-1">
                  <p className="m-0 px-3 py-1 text-xs font-semibold text-fg-secondary">{module.label}</p>
                  <ul className="m-0 list-none p-0">
                    {module.submodules.flatMap((submodule) =>
                      submodule.events.map((event) => {
                        const active = event.key === selected.event.key;
                        return (
                          <li key={event.key}>
                            <button
                              type="button"
                              aria-current={active ? 'true' : undefined}
                              onClick={() => select(event.key)}
                              className={cx(
                                'block w-full px-3 py-1.5 text-left text-sm transition-colors duration-150 ease-out motion-reduce:transition-none',
                                active
                                  ? 'bg-accent-brand-soft font-medium text-accent-brand'
                                  : 'text-fg hover:bg-surface-sunken',
                              )}
                            >
                              {event.label}
                            </button>
                          </li>
                        );
                      }),
                    )}
                  </ul>
                </div>
              ))}
            </nav>
          </Panel>

          <TemplatePreviewPanel
            key={selected.event.key}
            entry={selected}
            selfEmail={user?.email ?? null}
          />
        </div>
      )}
    </div>
  );
}
